import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import { addTodo, replaceTodo } from '@/features/todos/ducks';
import {
  Project,
  ProjectDetail,
  Todo,
  TodoStatus,
} from '@/infra/types/domain';
import {
  setProjects,
  addProject,
  setProject,
  setProjectDetail,
  setInboxOutlines,
  setInboxOutlinesLoading,
  removeProject,
  revertTodoPosition,
  replaceProjectTodoNode,
  setTodoStatusLocal,
  setListLoading,
  setDetailLoading,
  setError,
  FETCH_PROJECTS,
  CREATE_PROJECT,
  FETCH_PROJECT_DETAIL,
  UPDATE_PROJECT,
  ARCHIVE_PROJECT,
  UNARCHIVE_PROJECT,
  DELETE_PROJECT,
  CREATE_TASK,
  UPDATE_TODO_POSITION,
  SET_TODO_STATUS,
  FETCH_INBOX_OUTLINES,
  ProjectTodo,
} from './ducks';
import type { ProjectTodoNode } from '@/infra/types/domain';
import { selectChildrenMap } from './selectors';
import { pickSortOrder } from './dnd/sortOrder';

function* fetchProjectsSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'projects.fetch', ['projects'], action, function* () {
    yield put(setListLoading(true));
    try {
      const projects = (yield call(api.getProjects)) as Project[];
      yield* emitSagaPoint('api_getProjects_ok', { count: projects.length });
      yield put(setProjects(projects));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to fetch projects'));
      yield* emitSagaPoint('api_getProjects_err', { message: e?.message });
    }
  });
}

function* createProjectSaga(action: {
  type: string;
  payload: { title: string; description?: string };
}): Generator {
  yield call(withSagaFlow, 'projects.create', ['projects'], action, function* () {
    try {
      const project = (yield call(api.createProject, action.payload)) as Project;
      yield* emitSagaPoint('api_createProject_ok', { project_id: project.id });
      yield put(addProject(project));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to create project'));
      yield* emitSagaPoint('api_createProject_err', { message: e?.message });
    }
  });
}

function* fetchProjectDetailSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'projects.fetch_detail', ['projects'], action, function* () {
    yield put(setDetailLoading(true));
    try {
      const detail = (yield call(api.getProject, action.payload)) as ProjectDetail;
      yield* emitSagaPoint('api_getProject_ok', {
        node_count: detail.nodes?.length ?? 0,
        dep_count: detail.dependencies?.length ?? 0,
      });
      yield put(setProjectDetail(detail));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to fetch project'));
      yield put(setDetailLoading(false));
      yield* emitSagaPoint('api_getProject_err', { message: e?.message });
    }
  });
}

function* fetchInboxOutlinesSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'projects.fetch_inbox_outlines', ['projects'], action, function* () {
    yield put(setInboxOutlinesLoading(true));
    try {
      const outlines = (yield call(api.getProjectOutlines)) as ProjectDetail[];
      yield* emitSagaPoint('api_getProjectOutlines_ok', { count: outlines.length });
      yield put(setInboxOutlines(outlines));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to fetch project outlines'));
      yield put(setInboxOutlinesLoading(false));
      yield* emitSagaPoint('api_getProjectOutlines_err', { message: e?.message });
    }
  });
}

export function* updateProjectSaga(action: {
  type: string;
  payload: { id: string; updates: Record<string, unknown> };
}): Generator {
  yield call(withSagaFlow, 'projects.update', ['projects'], action, function* () {
    try {
      // Pext-style: PUT /projects/{id} returns the full Project post-mutation.
      // ProjectService.UpdateProject only touches the project row (title /
      // description / status / updated_at) — no cascade to todos, dependencies,
      // or archives — so replaying the response via setProject is the complete
      // delta. No FETCH_PROJECT_DETAIL or FETCH_PROJECTS refetch needed.
      // See feedback_saga_full_delta.
      const updated = (yield call(
        api.updateProject,
        action.payload.id,
        action.payload.updates,
      )) as Project;
      yield* emitSagaPoint('api_updateProject_ok', { project_id: updated.id });
      yield put(setProject(updated));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to update project'));
      yield* emitSagaPoint('api_updateProject_err', { message: e?.message });
    }
  });
}

export function* archiveProjectSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'projects.archive', ['projects'], action, function* () {
    try {
      // Pext-style: the server returns the full Project (now archived, with
      // refreshed counts). Replay via setProject — the project simply moves
      // from the Active to the Archived tab; its detached open tasks resurface
      // through the normal todos flow, so no projects-slice cascade is needed.
      const project = (yield call(api.archiveProject, action.payload)) as Project;
      yield* emitSagaPoint('api_archiveProject_ok', { project_id: project.id });
      yield put(setProject(project));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to archive project'));
      yield* emitSagaPoint('api_archiveProject_err', { message: e?.message });
    }
  });
}

export function* unarchiveProjectSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'projects.unarchive', ['projects'], action, function* () {
    try {
      const project = (yield call(api.unarchiveProject, action.payload)) as Project;
      yield* emitSagaPoint('api_unarchiveProject_ok', { project_id: project.id });
      yield put(setProject(project));
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to unarchive project'));
      yield* emitSagaPoint('api_unarchiveProject_err', { message: e?.message });
    }
  });
}

function* deleteProjectSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'projects.delete', ['projects'], action, function* () {
    try {
      yield call(api.deleteProject, action.payload);
      yield* emitSagaPoint('api_deleteProject_ok');
      yield put(removeProject(action.payload));
    } catch (e: any) {
      // 409 PROJECT_HAS_HISTORY: the project holds completed tasks and must be
      // archived, not deleted. The server message already says so — surface it.
      yield put(setError(e.message || 'Failed to delete project'));
      yield* emitSagaPoint('api_deleteProject_err', { code: e?.code, message: e?.message });
    }
  });
}

export function* createTaskSaga(action: {
  type: string;
  payload: { projectId: string; title: string; parentTodoId: string | null };
}): Generator {
  yield call(withSagaFlow, 'projects.create_task', ['projects'], action, function* () {
    const { projectId, title, parentTodoId } = action.payload;
    try {
      // Pext-style: POST /projects/{id}/tasks returns the created Todo
      // (always at project root, no sort_order yet — service.NewTodo
      // doesn't set ParentTodoID or SortOrder). Replay it into both the
      // todos slice and the projects-detail outline so the new row is
      // visible without a FETCH_PROJECT_DETAIL refetch. The detail entry
      // is synthesized from the Todo since the server hands back the bare
      // Todo, not a ProjectTodoNode — parent="" and sort_order=undefined
      // is the canonical "root, no manual order" shape that matches what
      // the wire would emit for a freshly-created task.
      // See feedback_saga_full_delta.
      const todo = (yield call(api.createTaskInProject, projectId, title)) as Todo;
      yield* emitSagaPoint('api_createTaskInProject_ok', { todo_id: todo.id });
      yield put(addTodo(todo));
      yield put(
        replaceProjectTodoNode({
          todo,
          parent_todo_id: '',
          sort_order: undefined,
        }),
      );

      // When the user added a subtask via "+ Add subtask" on an outline
      // row, the server creates the todo at root; we follow up with a
      // position update to nest it under the requested parent at the end
      // of its children. updateTodoPosition returns the canonical
      // ProjectTodoNode (post-rebalance sort_order included), which we
      // replay into both slices so the optimistic root entry converges
      // on server truth. On nest failure the row stays at root (the
      // replays above are intact); we surface an error instead of
      // silently dropping the move.
      if (parentTodoId) {
        const childrenMap = (yield select(selectChildrenMap)) as Record<string, ProjectTodo[]>;
        const siblings = childrenMap[parentTodoId] ?? [];
        const sortOrder = pickSortOrder(siblings, siblings.length);
        try {
          const node = (yield call(
            api.updateTodoPosition,
            projectId,
            todo.id,
            parentTodoId,
            sortOrder,
          )) as ProjectTodoNode;
          yield* emitSagaPoint('api_createTaskInProject_nested', {
            todo_id: todo.id,
            parent_id: parentTodoId,
          });
          yield put(replaceProjectTodoNode(node));
          if (node.todo) {
            yield put(replaceTodo(node.todo));
          }
        } catch (e: any) {
          yield put(setError(`Created task but couldn't nest: ${e?.message ?? 'unknown error'}`));
          yield* emitSagaPoint('api_createTaskInProject_nest_err', { message: e?.message });
        }
      }
    } catch (e: any) {
      yield put(setError(e.message || 'Failed to create task'));
      yield* emitSagaPoint('api_createTaskInProject_err', { message: e?.message });
    }
  });
}

export function* updateTodoPositionSaga(action: {
  type: string;
  payload: {
    projectId: string;
    todoId: string;
    parentId: string | null;
    sortOrder: number;
    prevParentId: string | null;
    prevSortOrder: number | null;
  };
}): Generator {
  yield call(withSagaFlow, 'projects.move_todo', ['projects'], action, function* () {
    const { projectId, todoId, parentId, sortOrder, prevParentId, prevSortOrder } =
      action.payload;
    try {
      const node = (yield call(
        api.updateTodoPosition,
        projectId,
        todoId,
        parentId,
        sortOrder,
      )) as ProjectTodoNode;
      yield* emitSagaPoint('api_updateTodoPosition_ok', { todo_id: todoId });
      // Pext-style server-truth replay: the server may have rebalanced
      // gap-spaced sort_orders during the move, so the canonical value
      // can differ from what we optimistically set. Replace the projects
      // detail node outright; also push the fresh Todo into the todos
      // slice so views deriving off todos byId see the same row.
      yield put(replaceProjectTodoNode(node));
      if (node.todo) {
        yield put(replaceTodo(node.todo));
      }
    } catch (e: any) {
      yield put(
        revertTodoPosition({ todoId, parentId: prevParentId, sortOrder: prevSortOrder }),
      );
      yield put(setError(e.message || 'Failed to move task'));
      yield* emitSagaPoint('api_updateTodoPosition_err', { message: e?.message });
    }
  });
}

export function* setTodoStatusSaga(action: {
  type: string;
  payload: { todoId: string; status: TodoStatus; prevStatus: TodoStatus };
}): Generator {
  yield call(withSagaFlow, 'projects.set_todo_status', ['projects'], action, function* () {
    const { todoId, status, prevStatus } = action.payload;
    // Optimistic: flip the chip immediately so the click feels responsive.
    // On server error, revert by pushing the pre-click status back in.
    yield put(setTodoStatusLocal({ todoId, status }));
    try {
      const updated = (yield call(api.updateTodo, todoId, { status })) as Todo;
      yield* emitSagaPoint('api_updateTodoStatus_ok', { todo_id: todoId, status });
      // Pext-style: saga is the boundary for server-truth replay. Status
      // transitions also mutate close_reason / completed_at server-side
      // (set on done, cleared on reopen). Replace the todos byId entry
      // from the response so views that derive off the todos slice see
      // the post-transition state without waiting for the next sync.
      // The projects-only optimistic cache (setTodoStatusLocal) stays —
      // the server agreed, no revert needed.
      yield put(replaceTodo(updated));
    } catch (e: any) {
      yield put(setTodoStatusLocal({ todoId, status: prevStatus }));
      yield put(setError(e.message || 'Failed to update status'));
      yield* emitSagaPoint('api_updateTodoStatus_err', { message: e?.message });
    }
  });
}

export function* projectsSaga(): Generator {
  yield takeLatest(FETCH_PROJECTS, fetchProjectsSaga);
  yield takeEvery(CREATE_PROJECT, createProjectSaga);
  yield takeLatest(FETCH_PROJECT_DETAIL, fetchProjectDetailSaga);
  yield takeLatest(FETCH_INBOX_OUTLINES, fetchInboxOutlinesSaga);
  yield takeEvery(UPDATE_PROJECT, updateProjectSaga);
  yield takeEvery(ARCHIVE_PROJECT, archiveProjectSaga);
  yield takeEvery(UNARCHIVE_PROJECT, unarchiveProjectSaga);
  yield takeEvery(DELETE_PROJECT, deleteProjectSaga);
  yield takeEvery(CREATE_TASK, createTaskSaga);
  yield takeEvery(UPDATE_TODO_POSITION, updateTodoPositionSaga);
  yield takeEvery(SET_TODO_STATUS, setTodoStatusSaga);
}
